import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Typography,
  Paper,
  Box,
  Button,
  TextField,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Snackbar,
  Alert,
  TablePagination,
  CircularProgress,
  SelectChangeEvent,
  Chip,
  useTheme,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  InputAdornment
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Bookmark as BookmarkIcon
} from '@mui/icons-material';
import SidebarLayout from '../../components/layout/SidebarLayout';
import MarcasService, {
  Marca,
  MarcaCreate,
  MarcaUpdate,
  MarcaFilter,
} from '../../services/marcasService';

// Interface para entidades de referência
interface EntidadeSimples {
  id: number;
  nome: string;
}

const MarcasPage: React.FC = () => {
  const theme = useTheme();
  
  // Estado de listagem
  const [marcas, setMarcas] = useState<Marca[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  // Estado de referências
  const [anunciantes, setAnunciantes] = useState<EntidadeSimples[]>([]);
  const [marcasPrincipais, setMarcasPrincipais] = useState<EntidadeSimples[]>([]);
  const [loadingReferencias, setLoadingReferencias] = useState(false);

  // Estado de filtros
  const [filter, setFilter] = useState<MarcaFilter>({});
  
  // Estado de formulários
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [currentMarca, setCurrentMarca] = useState<Marca | null>(null);
  const [newMarca, setNewMarca] = useState<MarcaCreate>({ nome: '' });
  const [editMarca, setEditMarca] = useState<MarcaUpdate>({});

  // Estado de mensagens
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error',
  });

  // Adicionar ref para debounce
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Carregar todas as referências necessárias
  const carregarReferencias = useCallback(async () => {
    try {
      setLoadingReferencias(true);
      // TODO: Implementar chamadas reais às APIs de anunciantes
      // Em um caso real, usaríamos:
      // const anunciantesData = await AnunciantesService.getAnunciantes();
      // setAnunciantes(anunciantesData);
      
      // Mock temporário para anunciantes
      setAnunciantes([
        { id: 1, nome: 'Anunciante 1' },
        { id: 2, nome: 'Anunciante 2' },
        { id: 3, nome: 'Anunciante 3' }
      ]);
      
      // Buscar marcas para uso como "marca principal"
      const marcasData = await MarcasService.getMarcas({}, 0, 100);
      setMarcasPrincipais(marcasData);
    } catch (error) {
      console.error('Erro ao carregar referências:', error);
      setSnackbar({
        open: true,
        message: 'Erro ao carregar dados de referência',
        severity: 'error',
      });
    } finally {
      setLoadingReferencias(false);
    }
  }, []);

  const fetchMarcas = useCallback(async () => {
    try {
      setLoading(true);
      const skip = page * rowsPerPage;
      const limit = rowsPerPage;
      
      const data = await MarcasService.getMarcas(filter, skip, limit);
      setMarcas(data);
      
      // Obter contagem total para paginação
      const count = await MarcasService.getMarcasCount(filter);
      setTotalCount(count);
    } catch (error) {
      console.error('Erro ao buscar marcas:', error);
      setSnackbar({
        open: true,
        message: 'Erro ao buscar marcas',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, filter]);

  useEffect(() => {
    // Buscar marcas ao iniciar a página ou mudar filtros/paginação
    fetchMarcas();
    // Carregar referências
    carregarReferencias();
  }, [fetchMarcas, carregarReferencias]);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterChange = (field: keyof MarcaFilter, value: string | number | undefined) => {
    // Limpar timeout anterior se existir
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Atualizar o filtro imediatamente na UI
    setFilter((prev) => ({
      ...prev,
      [field]: value || undefined, // Use undefined para remover o filtro vazio
    }));

    // Debounce: aguardar 500ms antes de aplicar a busca
    searchTimeoutRef.current = setTimeout(() => {
      setPage(0); // Resetar paginação ao mudar filtro
    }, 500);
  };

  // Função para limpar filtro de busca
  const clearSearchFilter = () => {
    setFilter((prev) => ({
      ...prev,
      nome: undefined
    }));
    setPage(0);
  };

  const handleCreateMarca = async () => {
    try {
      // Verificar campos obrigatórios
      if (!newMarca.nome || !newMarca.principal_id) {
        setSnackbar({
          open: true,
          message: 'Nome da marca e Marca Principal são obrigatórios',
          severity: 'error',
        });
        return;
      }

      const result = await MarcasService.createMarca(newMarca);
      setOpenCreateDialog(false);
      setSnackbar({
        open: true,
        message: 'Marca criada com sucesso',
        severity: 'success',
      });
      
      // Limpar formulário
      setNewMarca({ nome: '' });
      
      // Recarregar dados
      fetchMarcas();
      
      // Atualizar marcas principais para seleção
      carregarReferencias();
    } catch (error) {
      console.error('Erro ao criar marca:', error);
      setSnackbar({
        open: true,
        message: 'Erro ao criar marca',
        severity: 'error',
      });
    }
  };

  const handleOpenEditDialog = (marca: Marca) => {
    setCurrentMarca(marca);
    // Inicializar dados para edição
    setEditMarca({
      nome: marca.nome,
      anunciante_id: marca.anunciante_id,
      principal_id: marca.principal_id
    });
    
    setOpenEditDialog(true);
  };

  const handleEditMarca = async () => {
    if (!currentMarca) return;

    try {
      // Verificar campos obrigatórios
      if (!editMarca.nome) {
        setSnackbar({
          open: true,
          message: 'Nome da marca é obrigatório',
          severity: 'error',
        });
        return;
      }

      await MarcasService.updateMarca(currentMarca.id, editMarca);
      setOpenEditDialog(false);
      setSnackbar({
        open: true,
        message: 'Marca atualizada com sucesso',
        severity: 'success',
      });
      
      // Limpar estados
      setCurrentMarca(null);
      setEditMarca({});
      
      // Recarregar dados
      fetchMarcas();
      
      // Atualizar marcas principais para seleção
      carregarReferencias();
    } catch (error) {
      console.error('Erro ao atualizar marca:', error);
      setSnackbar({
        open: true,
        message: 'Erro ao atualizar marca',
        severity: 'error',
      });
    }
  };

  const handleOpenDeleteDialog = (marca: Marca) => {
    setCurrentMarca(marca);
    setOpenDeleteDialog(true);
  };

  const handleDeleteMarca = async () => {
    if (!currentMarca) return;

    try {
      await MarcasService.deleteMarca(currentMarca.id);
      setOpenDeleteDialog(false);
      setSnackbar({
        open: true,
        message: 'Marca excluída com sucesso',
        severity: 'success',
      });
      
      // Limpar estados
      setCurrentMarca(null);
      
      // Recarregar dados
      fetchMarcas();
      
      // Atualizar marcas principais para seleção
      carregarReferencias();
    } catch (error) {
      console.error('Erro ao excluir marca:', error);
      setSnackbar({
        open: true,
        message: 'Erro ao excluir marca',
        severity: 'error',
      });
    }
  };

  // Handler para alterações em selects
  const handleSelectChange = (event: SelectChangeEvent<number | string | undefined>, setter: Function) => {
    const value = event.target.value === '' ? undefined : Number(event.target.value);
    setter((prev: any) => ({
      ...prev,
      [event.target.name]: value
    }));
  };

  const handleSnackbarClose = () => {
    setSnackbar((prev) => ({
      ...prev,
      open: false,
    }));
  };

  return (
    <SidebarLayout>
      <Box p={3}>
        <Typography variant="h4" gutterBottom display="flex" alignItems="center">
          <BookmarkIcon sx={{ mr: 2 }} />
          Gerenciamento de Marcas
        </Typography>
        
        {/* Área de filtros */}
        <Paper 
          elevation={2} 
          sx={{ p: 2, mb: 3, backgroundColor: theme.palette.background.default }}
        >
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Buscar por nome"
                variant="outlined"
                size="small"
                value={filter.nome || ''}
                onChange={(e) => handleFilterChange('nome', e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                  endAdornment: filter.nome ? (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={clearSearchFilter}
                        edge="end"
                      >
                        <ClearIcon />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel id="anunciante-filter-label">Anunciante</InputLabel>
                <Select
                  labelId="anunciante-filter-label"
                  label="Anunciante"
                  name="anunciante_id"
                  value={filter.anunciante_id || ''}
                  onChange={(e) => handleFilterChange('anunciante_id', e.target.value === '' ? undefined : Number(e.target.value))}
                >
                  <MenuItem value="">Todos</MenuItem>
                  {anunciantes.map((anunciante) => (
                    <MenuItem key={anunciante.id} value={anunciante.id}>
                      {anunciante.nome}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setOpenCreateDialog(true)}
                sx={{ height: '100%' }}
              >
                Nova Marca
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {/* Tabela de Marcas */}
        <Paper elevation={3} sx={{ width: '100%', overflow: 'hidden' }}>
          <TableContainer sx={{ maxHeight: 'calc(100vh - 280px)' }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Nome</TableCell>
                  <TableCell>Anunciante</TableCell>
                  <TableCell>Marca Principal</TableCell>
                  <TableCell align="right">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      <CircularProgress size={24} />
                    </TableCell>
                  </TableRow>
                ) : marcas.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      Nenhuma marca encontrada
                    </TableCell>
                  </TableRow>
                ) : (
                  marcas.map((marca) => (
                    <TableRow key={marca.id} hover>
                      <TableCell>{marca.id}</TableCell>
                      <TableCell>{marca.nome}</TableCell>
                      <TableCell>
                        {anunciantes.find(a => a.id === marca.anunciante_id)?.nome || '-'}
                      </TableCell>
                      <TableCell>
                        {marcasPrincipais.find(m => m.id === marca.principal_id)?.nome || '-'}
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleOpenEditDialog(marca)}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleOpenDeleteDialog(marca)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>
      </Box>

      {/* Modal de Criação */}
      <Dialog
        open={openCreateDialog}
        onClose={() => setOpenCreateDialog(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Nova Marca</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Nome"
                name="nome"
                required
                value={newMarca.nome || ''}
                onChange={(e) => setNewMarca({ ...newMarca, nome: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel id="new-marca-principal-label">Marca Principal</InputLabel>
                <Select
                  labelId="new-marca-principal-label"
                  label="Marca Principal"
                  name="principal_id"
                  value={newMarca.principal_id || ''}
                  onChange={(e) => handleSelectChange(e, setNewMarca)}
                >
                  <MenuItem value="">Selecione...</MenuItem>
                  {marcasPrincipais.map((marca) => (
                    <MenuItem key={marca.id} value={marca.id}>
                      {marca.nome}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCreateDialog(false)}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={handleCreateMarca}
            disabled={!newMarca.nome || !newMarca.principal_id}
          >
            Criar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de Edição */}
      <Dialog
        open={openEditDialog}
        onClose={() => setOpenEditDialog(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Editar Marca</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Nome"
                name="nome"
                required
                value={editMarca.nome || ''}
                onChange={(e) => setEditMarca({ ...editMarca, nome: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel id="edit-anunciante-label">Anunciante</InputLabel>
                <Select
                  labelId="edit-anunciante-label"
                  label="Anunciante"
                  name="anunciante_id"
                  value={editMarca.anunciante_id || ''}
                  onChange={(e) => handleSelectChange(e, setEditMarca)}
                >
                  <MenuItem value="">Selecione...</MenuItem>
                  {anunciantes.map((anunciante) => (
                    <MenuItem key={anunciante.id} value={anunciante.id}>
                      {anunciante.nome}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel id="edit-marca-principal-label">Marca Principal</InputLabel>
                <Select
                  labelId="edit-marca-principal-label"
                  label="Marca Principal"
                  name="principal_id"
                  value={editMarca.principal_id || ''}
                  onChange={(e) => handleSelectChange(e, setEditMarca)}
                >
                  <MenuItem value="">Selecione...</MenuItem>
                  {marcasPrincipais
                    .filter(marca => currentMarca ? marca.id !== currentMarca.id : true)
                    .map((marca) => (
                      <MenuItem key={marca.id} value={marca.id}>
                        {marca.nome}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEditDialog(false)}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={handleEditMarca}
            disabled={!editMarca.nome}
          >
            Salvar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de Confirmação de Exclusão */}
      <Dialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Confirmar Exclusão</DialogTitle>
        <DialogContent>
          <Typography>
            Tem certeza que deseja excluir a marca "{currentMarca?.nome}"?
            Esta ação não pode ser desfeita.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>Cancelar</Button>
          <Button variant="contained" color="error" onClick={handleDeleteMarca}>
            Excluir
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar para mensagens */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </SidebarLayout>
  );
};

export default MarcasPage;
